import type { JSX } from 'react';

/**
 * Custom SVG icons for check-in steps, summary cards, and section headers.
 * Plush Citrine crystal style — soft, round, chunky shapes with
 * warm highlights and gentle opacity layers.  40×40 viewBox scaled
 * to requested size.
 */

interface IconProps {
  size?: number;
  color?: string;
  style?: React.CSSProperties;
}

/* ── helper ─────────────────────────────────────────────────── */
const sv = (size: number, style: React.CSSProperties | undefined, children: React.ReactNode) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}>
    {children}
  </svg>
);

/* ═══════════════════════════════════════════════════════════════
   CHECK-IN STEP ICONS  (plush Citrine style)
   ═══════════════════════════════════════════════════════════════ */

/** Step 1 — How are you today (warm sun with face) */
export function IconHowAreYou({ size = 24, color = 'currentColor', style }: IconProps) {
  return sv(size, style, <>
    {/* rays */}
    <line x1="20" y1="2" x2="20" y2="7" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.45" />
    <line x1="20" y1="33" x2="20" y2="38" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.45" />
    <line x1="2" y1="20" x2="7" y2="20" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.45" />
    <line x1="33" y1="20" x2="38" y2="20" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.45" />
    <line x1="7.3" y1="7.3" x2="10.8" y2="10.8" stroke={color} strokeWidth="2.2" strokeLinecap="round" opacity="0.35" />
    <line x1="29.2" y1="29.2" x2="32.7" y2="32.7" stroke={color} strokeWidth="2.2" strokeLinecap="round" opacity="0.35" />
    <line x1="32.7" y1="7.3" x2="29.2" y2="10.8" stroke={color} strokeWidth="2.2" strokeLinecap="round" opacity="0.35" />
    <line x1="7.3" y1="32.7" x2="10.8" y2="29.2" stroke={color} strokeWidth="2.2" strokeLinecap="round" opacity="0.35" />
    {/* body */}
    <circle cx="20" cy="20" r="11" fill={color} opacity="0.85" />
    <circle cx="20" cy="20" r="11" stroke={color} strokeWidth="1" opacity="0.3" />
    {/* highlight */}
    <ellipse cx="16" cy="16" rx="4" ry="5" fill="white" opacity="0.2" />
    {/* eyes */}
    <circle cx="16" cy="19" r="1.5" fill="white" opacity="0.9" />
    <circle cx="24" cy="19" r="1.5" fill="white" opacity="0.9" />
    {/* smile */}
    <path d="M15 24c2.2 2 7.8 2 10 0" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.8" />
  </>);
}

/** Step 2 — Sleep (plush crescent moon with star) */
export function IconSleep({ size = 24, color = 'currentColor', style }: IconProps) {
  return sv(size, style, <>
    <path d="M28 18c0 8-6 14-14 14A14 14 0 0 0 28 18z" fill={color} opacity="0.2" />
    <path d="M26 16a10 10 0 1 1-12-12 8 8 0 0 0 12 12z" fill={color} opacity="0.85" />
    <path d="M26 16a10 10 0 1 1-12-12 8 8 0 0 0 12 12z" stroke={color} strokeWidth="1" opacity="0.3" />
    <ellipse cx="12" cy="13" rx="3" ry="4" fill="white" opacity="0.18" />
    {/* star */}
    <path d="M30 8l1.2 2.4 2.6.4-1.9 1.8.45 2.6L30 14l-2.35 1.2.45-2.6-1.9-1.8 2.6-.4L30 8z" fill={color} opacity="0.7" />
    {/* tiny star */}
    <circle cx="34" cy="5" r="1.2" fill={color} opacity="0.5" />
  </>);
}

/** Step 3 — Stress (plush lightning bolt) */
export function IconStress({ size = 24, color = 'currentColor', style }: IconProps) {
  return sv(size, style, <>
    <path d="M22 3L10 19h7l-3 18 14-20h-8L22 3z" fill={color} opacity="0.85" />
    <path d="M22 3L10 19h7l-3 18 14-20h-8L22 3z" stroke={color} strokeWidth="1" opacity="0.3" />
    <path d="M18 10l-3 5h4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.25" />
  </>);
}

/** Feelings (plush heart — kept for backwards compat) */
export function IconFeeling({ size = 24, color = 'currentColor', style }: IconProps) {
  return sv(size, style, <>
    <path d="M20 35l-2.4-2.2C8.9 25.6 4 21.1 4 15.5 4 10.4 7.4 7 12.5 7c2.9 0 5.7 1.3 7.5 3.5C21.8 8.3 24.6 7 27.5 7 32.6 7 36 10.4 36 15.5c0 5.6-4.9 10.1-13.6 17.3L20 35z" fill={color} opacity="0.85" />
    <ellipse cx="15" cy="15" rx="4" ry="5" fill="white" opacity="0.2" />
  </>);
}

/** Step 4 — Worry (plush thought cloud with ?) */
export function IconWorry({ size = 24, color = 'currentColor', style }: IconProps) {
  return sv(size, style, <>
    {/* cloud body */}
    <ellipse cx="20" cy="17" rx="13" ry="10" fill={color} opacity="0.2" />
    <circle cx="13" cy="16" r="7" fill={color} opacity="0.85" />
    <circle cx="24" cy="14" r="8" fill={color} opacity="0.85" />
    <circle cx="18" cy="11" r="6" fill={color} opacity="0.85" />
    <ellipse cx="19" cy="20" rx="12" ry="6" fill={color} opacity="0.85" />
    <circle cx="13" cy="16" r="7" stroke={color} strokeWidth="0" />
    {/* highlight */}
    <ellipse cx="15" cy="11" rx="3.5" ry="3" fill="white" opacity="0.2" />
    {/* question mark */}
    <path d="M18 16a3 3 0 0 1 5.5 1.5c0 1.5-1.5 2-2.5 2.8" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.8" />
    <circle cx="21" cy="23" r="1" fill="white" opacity="0.8" />
    {/* thought dots */}
    <circle cx="11" cy="30" r="2" fill={color} opacity="0.5" />
    <circle cx="8" cy="34" r="1.3" fill={color} opacity="0.35" />
  </>);
}

/** Step 5 — Feeling blue (plush raindrop with face) */
export function IconBlue({ size = 24, color = 'currentColor', style }: IconProps) {
  return sv(size, style, <>
    <path d="M20 4C20 4 8 17 8 24a12 12 0 0 0 24 0C32 17 20 4 20 4z" fill={color} opacity="0.85" />
    <path d="M20 4C20 4 8 17 8 24a12 12 0 0 0 24 0C32 17 20 4 20 4z" stroke={color} strokeWidth="1" opacity="0.3" />
    <ellipse cx="16" cy="20" rx="3" ry="5" fill="white" opacity="0.2" />
    {/* sad eyes */}
    <circle cx="16" cy="23" r="1.3" fill="white" opacity="0.8" />
    <circle cx="24" cy="23" r="1.3" fill="white" opacity="0.8" />
    {/* frown */}
    <path d="M16 28c1.5-1.5 5.5-1.5 8 0" stroke="white" strokeWidth="1.3" strokeLinecap="round" fill="none" opacity="0.6" />
  </>);
}

/** Step 6 — Fatigue (plush low battery) */
export function IconFatigue({ size = 24, color = 'currentColor', style }: IconProps) {
  return sv(size, style, <>
    <rect x="3" y="10" width="29" height="20" rx="5" fill={color} opacity="0.85" />
    <rect x="3" y="10" width="29" height="20" rx="5" stroke={color} strokeWidth="1" opacity="0.3" />
    <rect x="32" y="16" width="5" height="8" rx="2" fill={color} opacity="0.6" />
    {/* charge level — low */}
    <rect x="6" y="14" width="8" height="12" rx="2.5" fill="white" opacity="0.35" />
    {/* highlight */}
    <ellipse cx="12" cy="14" rx="6" ry="3" fill="white" opacity="0.15" />
    {/* sleepy face */}
    <path d="M21 19c1 0 2 .5 2.5 0" stroke="white" strokeWidth="1.3" strokeLinecap="round" opacity="0.5" />
    <path d="M26 19c1 0 2 .5 2.5 0" stroke="white" strokeWidth="1.3" strokeLinecap="round" opacity="0.5" />
  </>);
}

/** Step 7 — Pain (plush bandage cross) */
export function IconPain({ size = 24, color = 'currentColor', style }: IconProps) {
  return sv(size, style, <>
    {/* cross bands */}
    <rect x="1" y="13" width="38" height="14" rx="7" fill={color} opacity="0.4" />
    <rect x="13" y="1" width="14" height="38" rx="7" fill={color} opacity="0.4" />
    {/* center pad */}
    <rect x="12" y="12" width="16" height="16" rx="4" fill={color} opacity="0.85" />
    <rect x="12" y="12" width="16" height="16" rx="4" stroke={color} strokeWidth="1" opacity="0.3" />
    {/* highlight */}
    <ellipse cx="17" cy="16" rx="3" ry="2.5" fill="white" opacity="0.2" />
    {/* dots */}
    <circle cx="17" cy="18" r="1.3" fill="white" opacity="0.6" />
    <circle cx="23" cy="18" r="1.3" fill="white" opacity="0.6" />
    <circle cx="17" cy="24" r="1.3" fill="white" opacity="0.6" />
    <circle cx="23" cy="24" r="1.3" fill="white" opacity="0.6" />
  </>);
}

/** Step 8 — Treatment (plush handshake / caring hands) */
export function IconTreatment({ size = 24, color = 'currentColor', style }: IconProps) {
  return sv(size, style, <>
    {/* left hand */}
    <path d="M4 22c0-3 2-5 5-6l6 4 6-4c3 1 5 3 5 6v4c0 2-1 3-3 3H7c-2 0-3-1-3-3v-4z" fill={color} opacity="0.85" />
    <path d="M4 22c0-3 2-5 5-6l6 4 6-4c3 1 5 3 5 6v4c0 2-1 3-3 3H7c-2 0-3-1-3-3v-4z" stroke={color} strokeWidth="1" opacity="0.3" />
    {/* highlight */}
    <ellipse cx="14" cy="20" rx="4" ry="3" fill="white" opacity="0.18" />
    {/* heart above */}
    <path d="M20 7l-.8-.7C16.7 4 15 5.1 15 7.2c0 1.9 1.6 3.4 4.5 5.8l.5.4.5-.4c2.9-2.4 4.5-3.9 4.5-5.8C25 5.1 23.3 4 20.8 6.3L20 7z" fill={color} opacity="0.6" />
  </>);
}

/** Step 9 — What helped (plush shield with plus) */
export function IconWhatHelped({ size = 24, color = 'currentColor', style }: IconProps) {
  return sv(size, style, <>
    <path d="M20 3L6 9v8c0 9 6 16 14 18 8-2 14-9 14-18V9L20 3z" fill={color} opacity="0.85" />
    <path d="M20 3L6 9v8c0 9 6 16 14 18 8-2 14-9 14-18V9L20 3z" stroke={color} strokeWidth="1" opacity="0.3" />
    <ellipse cx="16" cy="14" rx="4" ry="5" fill="white" opacity="0.18" />
    {/* plus */}
    <line x1="20" y1="14" x2="20" y2="24" stroke="white" strokeWidth="3" strokeLinecap="round" opacity="0.85" />
    <line x1="15" y1="19" x2="25" y2="19" stroke="white" strokeWidth="3" strokeLinecap="round" opacity="0.85" />
  </>);
}

/** Step 10 — Body map (plush body silhouette) */
export function IconBodyMap({ size = 24, color = 'currentColor', style }: IconProps) {
  return sv(size, style, <>
    {/* head */}
    <circle cx="20" cy="8" r="5" fill={color} opacity="0.85" />
    <circle cx="20" cy="8" r="5" stroke={color} strokeWidth="1" opacity="0.3" />
    <ellipse cx="18" cy="7" rx="2" ry="2.5" fill="white" opacity="0.18" />
    {/* body */}
    <path d="M12 14c-2 0-4 1.5-4 4l-.5 8c0 1.5 1.2 2.5 2.5 2.5h2l1 8c0 1 1 2 2.5 2h5c1.5 0 2.5-1 2.5-2l1-8h2c1.3 0 2.5-1 2.5-2.5L27 18c0-2.5-2-4-4-4h-11z" fill={color} opacity="0.85" />
    <path d="M12 14c-2 0-4 1.5-4 4l-.5 8c0 1.5 1.2 2.5 2.5 2.5h2l1 8c0 1 1 2 2.5 2h5c1.5 0 2.5-1 2.5-2l1-8h2c1.3 0 2.5-1 2.5-2.5L27 18c0-2.5-2-4-4-4h-11z" stroke={color} strokeWidth="1" opacity="0.3" />
    <ellipse cx="16" cy="18" rx="3" ry="4" fill="white" opacity="0.15" />
  </>);
}

/** Emotion map (plush brain — kept for backwards compat) */
export function IconEmotionMap({ size = 24, color = 'currentColor', style }: IconProps) {
  return sv(size, style, <>
    <path d="M20 3c-5 0-9 2.5-9.5 6-3.5 1-6 4-6 8 0 3.5 2 6 4 7.5v6c0 2.5 2 5 5 5h2" fill={color} opacity="0.65" />
    <path d="M20 3c5 0 9 2.5 9.5 6 3.5 1 6 4 6 8 0 3.5-2 6-4 7.5v6c0 2.5-2 5-5 5h-2" fill={color} opacity="0.85" />
    <line x1="20" y1="3" x2="20" y2="37" stroke="white" strokeWidth="1" opacity="0.15" />
    <ellipse cx="15" cy="14" rx="3" ry="4" fill="white" opacity="0.15" />
  </>);
}


/* ═══════════════════════════════════════════════════════════════
   WATERS CHECK ICONS  (plush crystal style)
   ═══════════════════════════════════════════════════════════════ */

/** Waters Check — Calm waters (citrine clam on beach with pearl) */
export function IconCalmWaters({ size = 24, color = '#c89838', style }: IconProps) {
  return sv(size, style, <>
    {/* warm glow */}
    <circle cx="20" cy="18" r="13" fill={color} opacity="0.1" />
    {/* sandy shore */}
    <ellipse cx="20" cy="33" rx="17" ry="4" fill="#e0b850" opacity="0.25" />
    {/* clam body — citrine teardrop */}
    <path d="M10 24c0-5 4-11 10-11s10 6 10 11c0 2-4.5 3.5-10 3.5S10 26 10 24z" fill={color} opacity="0.85" />
    <path d="M10 24c0-5 4-11 10-11s10 6 10 11c0 2-4.5 3.5-10 3.5S10 26 10 24z" stroke={color} strokeWidth="1" opacity="0.3" />
    {/* clam lid */}
    <path d="M11 24c.5-7 4-10 9-10s8.5 3 9 10" fill="#e0b850" opacity="0.6" />
    {/* shell ridges */}
    <path d="M15 22c.7-3.5 2.5-6.5 5-6.5" stroke="white" strokeWidth="0.8" strokeLinecap="round" opacity="0.3" />
    <path d="M20 15.5v9" stroke="white" strokeWidth="0.8" strokeLinecap="round" opacity="0.25" />
    <path d="M25 22c-.7-3.5-2.5-6.5-5-6.5" stroke="white" strokeWidth="0.8" strokeLinecap="round" opacity="0.3" />
    {/* highlight */}
    <ellipse cx="16" cy="18" rx="3" ry="4" fill="white" opacity="0.18" />
    {/* pearl */}
    <circle cx="20" cy="22" r="3" fill="#d8ccb8" opacity="0.9" />
    <circle cx="20" cy="22" r="3" stroke="#c0b098" strokeWidth="0.8" opacity="0.3" />
    <ellipse cx="19" cy="21" rx="1.2" ry="1" fill="white" opacity="0.5" />
    {/* gentle water */}
    <path d="M3 30c4-1 6.5 .5 10 0s6.5-1 10 0 5.5.5 8 0" stroke="#7090b0" strokeWidth="1" strokeLinecap="round" opacity="0.3" />
  </>);
}

/** Waters Check — Choppy (sapphire waves, crystal spray) */
export function IconChoppyWaters({ size = 24, color = '#3870a0', style }: IconProps) {
  return sv(size, style, <>
    {/* glow under waves */}
    <ellipse cx="20" cy="22" rx="17" ry="10" fill={color} opacity="0.08" />
    {/* wave 1 — main swell */}
    <path d="M1 18c3.5-4 7.5-4 12 0s8.5 4 12 0 7-4 10 0" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="3" strokeLinecap="round" />
    {/* wave 2 */}
    <path d="M3 25c3-2.5 6.5-2.5 10 0s7 2.5 10 0 6.5-2.5 10 0" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.55" />
    {/* wave 3 */}
    <path d="M5 31c4-1.5 7 1 11 0s6-1.5 9 0 5 1 8 0" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
    {/* crystal spray — sapphire sparkles */}
    <circle cx="12" cy="12" r="1.5" fill="#5090c0" opacity="0.6" />
    <circle cx="27" cy="13" r="1.2" fill="#5090c0" opacity="0.5" />
    <circle cx="18" cy="10" r="1" fill="#b0d8f8" opacity="0.4" />
    {/* highlight on main wave */}
    <ellipse cx="14" cy="16" rx="3.5" ry="2" fill="white" opacity="0.15" />
  </>);
}

/** Waters Check — Rough seas (amethyst deep, crashing swell) */
export function IconRoughSeas({ size = 24, color = '#5a4080', style }: IconProps) {
  return sv(size, style, <>
    {/* deep water fill */}
    <path d="M1 22c2.5-8 7-13 11-13 3.5 0 5 4 8.5 4s4-2.5 7-2.5c5 0 9 6.5 11 11.5v14H1z" fill={color} opacity="0.2" />
    {/* crashing wave crest */}
    <path d="M1 22c2.5-8 7-13 11-13 3.5 0 5 4 8.5 4s4-2.5 7-2.5c5 0 9 6.5 11 11.5" stroke={color} strokeWidth="3.5" strokeLinecap="round" />
    {/* wave curl */}
    <path d="M28 10c1.5-2.5 2.5-5.5 1-7" stroke="#7858a8" strokeWidth="2.5" strokeLinecap="round" opacity="0.7" />
    {/* amethyst spray */}
    <circle cx="30" cy="5" r="1.5" fill="#9878c0" opacity="0.6" />
    <circle cx="24" cy="7" r="1" fill="#9878c0" opacity="0.45" />
    <circle cx="33" cy="9" r="1.2" fill="#7858a8" opacity="0.5" />
    {/* highlight */}
    <ellipse cx="14" cy="15" rx="3" ry="3.5" fill="white" opacity="0.12" />
    {/* undertow */}
    <path d="M3 29c5-2 10 .5 15-.8s8 .5 13-.8" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.3" />
    <path d="M5 34c5-.8 8 .8 12 0s7-.8 10 0" stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity="0.18" />
  </>);
}

/** Waters Check — SOS (rose quartz life ring) */
export function IconSOS({ size = 24, color = 'var(--color-secondary)', style }: IconProps) {
  return sv(size, style, <>
    {/* soft glow */}
    <circle cx="20" cy="20" r="18" fill={color} opacity="0.08" />
    {/* ring base */}
    <circle cx="20" cy="20" r="13" stroke={color} strokeWidth="6.5" opacity="0.25" />
    {/* rose quartz segments */}
    <path d="M20 7a13 13 0 0 1 9.19 3.81l-4.6 4.6A6.5 6.5 0 0 0 20 13.5V7z" fill={color} opacity="0.85" />
    <path d="M20 33a13 13 0 0 1-9.19-3.81l4.6-4.6A6.5 6.5 0 0 0 20 26.5V33z" fill={color} opacity="0.85" />
    <path d="M7 20a13 13 0 0 1 3.81-9.19l4.6 4.6A6.5 6.5 0 0 0 13.5 20H7z" fill={color} opacity="0.85" />
    <path d="M33 20a13 13 0 0 1-3.81 9.19l-4.6-4.6A6.5 6.5 0 0 0 26.5 20H33z" fill={color} opacity="0.85" />
    {/* pearl-white alternating segments */}
    <path d="M20 7a13 13 0 0 0-9.19 3.81l4.6 4.6A6.5 6.5 0 0 1 20 13.5V7z" fill="#d8a0b2" opacity="0.45" />
    <path d="M33 20a13 13 0 0 0-3.81-9.19l-4.6 4.6A6.5 6.5 0 0 1 26.5 20H33z" fill="#d8a0b2" opacity="0.45" />
    <path d="M20 33a13 13 0 0 0 9.19-3.81l-4.6-4.6A6.5 6.5 0 0 1 20 26.5V33z" fill="#d8a0b2" opacity="0.45" />
    <path d="M7 20a13 13 0 0 0 3.81 9.19l4.6-4.6A6.5 6.5 0 0 1 13.5 20H7z" fill="#d8a0b2" opacity="0.45" />
    {/* inner hole */}
    <circle cx="20" cy="20" r="5" fill="none" stroke="#e8c0d0" strokeWidth="1" opacity="0.35" />
    {/* cheek highlight */}
    <ellipse cx="15" cy="14" rx="2.5" ry="3" fill="white" opacity="0.18" />
  </>);
}


/* ═══════════════════════════════════════════════════════════════
   SECTION HEADER ICONS  (Patterns page — plush style)
   ═══════════════════════════════════════════════════════════════ */

/** Crisis / plush alert light */
export function IconCrisis({ size = 24, color = 'currentColor', style }: IconProps) {
  return sv(size, style, <>
    {/* base */}
    <rect x="12" y="28" width="16" height="8" rx="3" fill={color} opacity="0.35" />
    {/* light dome */}
    <circle cx="20" cy="18" r="12" fill={color} opacity="0.85" />
    <circle cx="20" cy="18" r="12" stroke={color} strokeWidth="1" opacity="0.3" />
    {/* glow rays */}
    <line x1="20" y1="2" x2="20" y2="5" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.5" />
    <line x1="32" y1="10" x2="30" y2="12" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.5" />
    <line x1="8" y1="10" x2="10" y2="12" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.5" />
    {/* highlight */}
    <ellipse cx="16" cy="14" rx="3.5" ry="4" fill="white" opacity="0.22" />
    {/* exclamation */}
    <line x1="20" y1="13" x2="20" y2="19" stroke="white" strokeWidth="3" strokeLinecap="round" opacity="0.9" />
    <circle cx="20" cy="24" r="1.5" fill="white" opacity="0.9" />
  </>);
}

/** Trend line / plush chart */
export function IconTrend({ size = 24, color = 'currentColor', style }: IconProps) {
  return sv(size, style, <>
    {/* filled area under */}
    <path d="M4 32l8-10 6 5 10-15 8 6V32H4z" fill={color} opacity="0.15" />
    {/* line */}
    <path d="M4 32l8-10 6 5 10-15 8 6" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
    {/* dots */}
    <circle cx="12" cy="22" r="3" fill={color} opacity="0.85" />
    <circle cx="12" cy="22" r="3" stroke={color} strokeWidth="1" opacity="0.3" />
    <ellipse cx="11" cy="21" rx="1" ry="1.2" fill="white" opacity="0.25" />
    <circle cx="18" cy="27" r="3" fill={color} opacity="0.85" />
    <ellipse cx="17" cy="26" rx="1" ry="1.2" fill="white" opacity="0.25" />
    <circle cx="28" cy="12" r="3" fill={color} opacity="0.85" />
    <ellipse cx="27" cy="11" rx="1" ry="1.2" fill="white" opacity="0.25" />
    <circle cx="36" cy="18" r="3" fill={color} opacity="0.85" />
    <ellipse cx="35" cy="17" rx="1" ry="1.2" fill="white" opacity="0.25" />
  </>);
}

/** Star / plush sparkle for "What Helped Most" */
export function IconStar({ size = 24, color = 'currentColor', style }: IconProps) {
  return sv(size, style, <>
    <path d="M20 3l4.8 9.7 10.7 1.6-7.75 7.5 1.83 10.7L20 27.8l-9.58 4.7 1.83-10.7-7.75-7.5 10.7-1.6L20 3z" fill={color} opacity="0.85" />
    <path d="M20 3l4.8 9.7 10.7 1.6-7.75 7.5 1.83 10.7L20 27.8l-9.58 4.7 1.83-10.7-7.75-7.5 10.7-1.6L20 3z" stroke={color} strokeWidth="1" opacity="0.3" />
    <ellipse cx="16" cy="14" rx="3" ry="4" fill="white" opacity="0.2" />
    {/* inner sparkle */}
    <circle cx="20" cy="18" r="3" fill="white" opacity="0.2" />
  </>);
}

/* IconStreak removed 2026-05-21 — superseded by CrystalIcons.IconStreak.
 * The Crystal Guide visual system is now canonical for streak iconography.
 * Callsites that used CheckinIcons.IconStreak have been moved to
 * ./CrystalIcons.IconStreak (different prop API: ariaLabel + emptyState
 * instead of color + style; the new icon themes via CSS custom properties
 * so dark + high-contrast modes carry over automatically). */


/* ═══════════════════════════════════════════════════════════════ */
/** Map step number → icon component */
export const CHECKIN_STEP_ICONS: Record<number, (props: IconProps) => JSX.Element> = {
  1: IconHowAreYou,
  2: IconSleep,
  3: IconStress,
  4: IconWorry,
  5: IconBlue,
  6: IconFatigue,
  7: IconPain,
  8: IconBodyMap,
  9: IconTreatment,
  10: IconWhatHelped,
};
